import { z } from 'zod/v4';

import { buttonHorizontalPositionSchema } from '@sites/entity-core';

/**
 * Absurdity guard only — the editor's width input enforces no upper bound (same policy as image's
 * `maxWidth` and indent's height).
 */
const MAX_WIDTH_LIMIT = 5000;

export const videoUrlSchema = z
    .string()
    .min(1)
    .describe(
        'Link to the video on YouTube or Vimeo — a watch, share, embed, shorts or live link, whichever ' +
            'the user has. The source (YouTube vs Vimeo), the embeddable form of the link, the poster ' +
            'image and the vertical/wide aspect ratio are all worked out from it, so paste the link as ' +
            'given rather than rewriting it. Videos uploaded to the SendPulse file manager cannot be ' +
            'chosen here — that needs a file pick in the editor.'
    );

/**
 * THE video widget data shape as AI tools accept it — one source of truth shared by
 * `create_video_widget`/`update_video_widget`, so their field surfaces cannot drift (same reasoning
 * as image's and text's twins). Lives here because both `apps/mcp-backend`'s mutations and
 * `apps/mcp-server`'s tools need the identical shape, and neither may import the other.
 *
 * **Four fields out of the entity's eleven, because the other seven are not authorable.** Read the
 * editor's control before the entity schema and the split is obvious: `videoType`, `videoUrl`,
 * `previewUrl` and `isShorts` are all DERIVED from the one link the user pastes
 * (`VideoControlComponent.setVideoUrl`), and `videoFile`/`customPreviewFile`/`placeholder` only ever
 * come from a file pick or the UI itself. Exposing the derived four would let a model set a YouTube
 * `videoType` next to a Vimeo `videoUrl` — a combination the entity schema's own `superRefine`
 * rejects, turning a reasonable request into a validation failure the model cannot diagnose.
 *
 * So the AI surface is the link plus the three layout knobs, and
 * `video-widget-patch.ts` does what the control does with it.
 */
export const partialVideoWidgetDataSchema = z.object({
    url: videoUrlSchema.optional(),
    maxWidth: z
        .number()
        .int()
        .min(1)
        .max(MAX_WIDTH_LIMIT)
        .nullable()
        .optional()
        .describe(
            'Widest the player may render, in PIXELS (not a percentage). Omit or null to fill the ' +
                "column's width, which is what a fresh video does."
        ),
    horizontalAlign: buttonHorizontalPositionSchema
        .optional()
        .describe('Alignment within the column: "L", "M" (default) or "R". Only visible once maxWidth is set.'),
    previewUrl: z
        .string()
        .optional()
        .describe(
            'Poster image shown before playback. Omit it: for YouTube it is derived from the link, and ' +
                'for Vimeo the editor asks Vimeo for the thumbnail — pass one only to override that with ' +
                'a specific image.'
        ),
});

export type PartialVideoWidgetData = z.infer<typeof partialVideoWidgetDataSchema>;
